import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export type ProgressStatus = 'empty' | 'in-progress' | 'complete';
export type ProgressLabelPosition = 'false' | 'left' | 'right';

/**
 * A horizontal progress bar indicator for showing completion status,
 * multi-step workflows, and upload progress.
 *
 * The fill width and status are driven by the `percent` property
 * (a number from 0–100). The component clamps the value, reflects a computed
 * `status` attribute (`empty` | `in-progress` | `complete`) for per-state
 * color theming via CSS, and keeps `aria-valuenow` in sync. An optional
 * label displaying the percentage can appear to the left or right of the track.
 *
 * @element asc-progress-bar
 *
 * @csspart base  - The root flex-row wrapper.
 * @csspart track - The full-width background track.
 * @csspart fill  - The animated fill bar; width is bound to `percent`.
 * @csspart label - The label wrapper span.
 *
 * @cssprop [--asc-progress-bar-track-height=var(--asc-spacing-md)] - Height of the track and fill bar. Default: `8px`.
 * @cssprop [--asc-progress-bar-track-radius=var(--asc-radius-full)] - Border radius of the track. Default: pill shape.
 * @cssprop [--asc-progress-bar-track-color=var(--asc-bg-secondary)] - Background color of the unfilled track.
 * @cssprop [--asc-progress-bar-fill-color=var(--asc-fg-brand-primary)] - Fill color for the in-progress state.
 * @cssprop [--asc-progress-bar-fill-color-empty=var(--asc-progress-bar-fill-color)] - Fill color when status is `empty` (0%).
 * @cssprop [--asc-progress-bar-fill-color-complete=var(--asc-progress-bar-fill-color)] - Fill color when status is `complete` (100%).
 * @cssprop [--asc-progress-bar-fill-radius=var(--asc-radius-full)] - Border radius of the fill element.
 * @cssprop [--asc-progress-bar-transition-duration=300ms] - Duration of the fill width animation.
 * @cssprop [--asc-progress-bar-gap=var(--asc-spacing-lg)] - Gap between the track and the label.
 * @cssprop [--asc-progress-bar-label-color=var(--asc-text-secondary)] - Label text color.
 * @cssprop [--asc-progress-bar-label-font-size=var(--asc-font-size-text-sm)] - Label font size.
 * @cssprop [--asc-progress-bar-label-font-weight=var(--asc-font-weight-medium)] - Label font weight.
 * @cssprop [--asc-progress-bar-label-line-height=var(--asc-line-height-text-sm)] - Label line height.
 * @cssprop [--asc-progress-bar-label-font-family=var(--asc-font-family-body)] - Label font family.
 */
@customElement('asc-progress-bar')
export class AscProgressBar extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    [part='base'] {
      display: flex;
      align-items: center;
      gap: var(--asc-progress-bar-gap, var(--asc-spacing-lg));
    }

    /* Hide label when label="false" (default) or attribute is absent */
    :host([label='false']) [part='label'],
    :host(:not([label])) [part='label'] {
      display: none;
    }

    /* Move label before the track when label="left" */
    :host([label='left']) [part='label'] {
      order: -1; /* track is first in DOM; -1 pulls label before it */
    }

    [part='track'] {
      flex: 1 0 0;
      position: relative;
      height: var(--asc-progress-bar-track-height, var(--asc-spacing-md));
      background-color: var(--asc-progress-bar-track-color, var(--asc-bg-secondary));
      border-radius: var(--asc-progress-bar-track-radius, var(--asc-radius-full));
      overflow: hidden;
    }

    [part='fill'] {
      position: absolute;
      inset-block: 0;
      left: 0;
      height: 100%;
      /* Minimum width = track height so 0% renders as a small pill/dot */
      min-width: var(--asc-progress-bar-track-height, var(--asc-spacing-md));
      background-color: var(--asc-progress-bar-fill-color, var(--asc-fg-brand-primary));
      border-radius: var(--asc-progress-bar-fill-radius, var(--asc-radius-full));
      transition: width var(--asc-progress-bar-transition-duration, 300ms) ease;
    }

    :host([status='empty']) [part='fill'] {
      background-color: var(
        --asc-progress-bar-fill-color-empty,
        var(--asc-progress-bar-fill-color, var(--asc-fg-brand-primary))
      );
    }

    :host([status='complete']) [part='fill'] {
      background-color: var(
        --asc-progress-bar-fill-color-complete,
        var(--asc-progress-bar-fill-color, var(--asc-fg-brand-primary))
      );
    }

    [part='label'] {
      flex-shrink: 0;
      font-family: var(--asc-progress-bar-label-font-family, var(--asc-font-family-body));
      font-size: var(--asc-progress-bar-label-font-size, var(--asc-font-size-text-sm));
      font-weight: var(--asc-progress-bar-label-font-weight, var(--asc-font-weight-medium));
      line-height: var(--asc-progress-bar-label-line-height, var(--asc-line-height-text-sm));
      color: var(--asc-progress-bar-label-color, var(--asc-text-secondary));
      white-space: nowrap;
    }
  `;

  /** Numeric progress value between 0 and 100. Values outside this range are clamped. */
  @property({ type: Number, attribute: 'percent', reflect: true }) percent = 0;

  /** Controls visibility and position of the percentage value as a visual label. */
  @property({ type: String, reflect: true }) label: ProgressLabelPosition = 'false';

  private get _clamped(): number {
    return Math.min(100, Math.max(0, this.percent));
  }

  private get _statusValue(): ProgressStatus {
    return this._clamped === 0 ? 'empty' : this._clamped === 100 ? 'complete' : 'in-progress';
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute('role', 'progressbar');
    this.setAttribute('aria-valuemin', '0');
    this.setAttribute('aria-valuemax', '100');
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Progress');
    }
  }

  override updated(): void {
    this.setAttribute('aria-valuenow', String(this._clamped));
    this.setAttribute('status', this._statusValue);
  }

  override render() {
    return html`
      <div part="base">
        <div part="track">
          <div part="fill" style="width: ${this._clamped}%"></div>
        </div>
        <span part="label" aria-hidden="true">${this._clamped}%</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-progress-bar': AscProgressBar;
  }
}
